/**
 * WebUSB helpers for ESC/POS thermal printers.
 *
 * Why: On the cloud deployment (crestpos.net) the backend can't reach the
 * physical POS terminal's USB ports. WebUSB lets the browser at the
 * terminal claim the printer directly and transferOut() the ESC/POS bytes
 * the backend renders. No local bridge daemon needed.
 *
 * Support: Chromium-family browsers (Chrome/Edge/Brave) on HTTPS only.
 * Firefox and Safari do NOT implement WebUSB — those callers should fall
 * back to `window.print()` (browser dialog).
 *
 * Printer class: ESC/POS thermal printers advertise USB printer class
 * (`classCode: 7`). Some cheap Chinese units instead expose a vendor-
 * specific interface — we filter on classCode 7 by default but fall back
 * to "no filter" when the user re-tries pairing.
 */

const USB_PRINTER_CLASS = 0x07;

/**
 * @returns {boolean} True when WebUSB is available in this browser.
 */
export const webusbSupported = () =>
  typeof navigator !== "undefined" && !!navigator.usb;

/**
 * Prompt the user to pick a USB printer. Chrome shows an OS-level device
 * picker; the user must physically click the device they want.
 *
 * @returns {Promise<{vendorId, productId, serialNumber, displayName} | null>}
 */
export async function pairPrinter() {
  if (!webusbSupported()) {
    throw new Error(
      "This browser doesn't support WebUSB. Use Chrome, Edge, or Brave.",
    );
  }
  // Try printer-class filter first — narrows the picker to actual printers.
  let device;
  try {
    device = await navigator.usb.requestDevice({
      filters: [{ classCode: USB_PRINTER_CLASS }],
    });
  } catch {
    // User dismissed the picker, or no printer-class device present. Retry
    // with an empty filter so a vendor-specific unit still shows up.
    device = await navigator.usb.requestDevice({ filters: [] });
  }
  if (!device) return null;
  return {
    vendorId: device.vendorId,
    productId: device.productId,
    serialNumber: device.serialNumber || "",
    displayName:
      device.productName ||
      device.manufacturerName ||
      `USB ${device.vendorId.toString(16)}:${device.productId.toString(16)}`,
  };
}

/**
 * Find a previously-paired device that matches the stored identity.
 * Called before every print — the browser remembers pairings per-origin
 * so subsequent visits don't need to re-prompt.
 */
async function findPairedDevice({ vendorId, productId, serialNumber }) {
  if (!webusbSupported()) return null;
  const list = await navigator.usb.getDevices();
  return (
    list.find(
      (d) =>
        d.vendorId === Number(vendorId) &&
        d.productId === Number(productId) &&
        (!serialNumber || d.serialNumber === serialNumber),
    ) || null
  );
}

/**
 * Locate the OUT endpoint on the printer's interface — the one bytes flow
 * TO the printer over. Almost every ESC/POS device has exactly one; we
 * pick the first bulk-OUT we find and remember it.
 */
function pickBulkOutEndpoint(device) {
  for (const conf of device.configurations) {
    for (const iface of conf.interfaces) {
      for (const alt of iface.alternates) {
        for (const ep of alt.endpoints) {
          if (ep.direction === "out" && ep.type === "bulk") {
            return {
              configuration: conf.configurationValue,
              interface: iface.interfaceNumber,
              endpoint: ep.endpointNumber,
            };
          }
        }
      }
    }
  }
  return null;
}

/**
 * Send raw ESC/POS bytes to a paired WebUSB printer.
 *
 * @param {object} pairing  {vendorId, productId, serialNumber}
 * @param {Uint8Array} bytes  raw ESC/POS payload
 * @returns {Promise<{bytesWritten:number}>}
 */
export async function sendBytes(pairing, bytes) {
  if (!webusbSupported()) {
    throw new Error("WebUSB not supported in this browser.");
  }
  const device = await findPairedDevice(pairing);
  if (!device) {
    throw new Error(
      "USB printer not paired in this browser yet. Open Settings → Equipment → your printer → Pair.",
    );
  }
  const ep = pickBulkOutEndpoint(device);
  if (!ep) {
    throw new Error("Couldn't find a bulk-OUT endpoint on this device.");
  }
  try {
    await device.open();
  } catch (e) {
    // "Access denied" almost always means another driver on the OS still
    // owns the printer:
    //   • Windows — the print-spooler USB backend is bound. Fix: run
    //     Zadig (https://zadig.akeo.ie) → pick the printer → replace the
    //     driver with WinUSB. Chrome can then open() it.
    //   • Linux — the usblp kernel module has grabbed it. Fix: `sudo
    //     rmmod usblp` (or blacklist it in /etc/modprobe.d/) + add a
    //     udev rule for the vendorId so the user has read/write access
    //     to /dev/bus/usb/*.
    //   • macOS — CUPS may be printing. Fix: remove the printer from
    //     "Printers & Scanners" so CUPS releases it.
    const msg = String(e?.message || e);
    if (/access denied|not permitted|permission/i.test(msg)) {
      throw new Error(
        "USB access denied — another app is holding the printer. On Windows: install WinUSB driver via Zadig (https://zadig.akeo.ie). On Linux: blacklist the usblp module. On macOS: remove the printer from System Preferences → Printers & Scanners.",
      );
    }
    throw e;
  }
  try {
    if (
      device.configuration === null ||
      device.configuration.configurationValue !== ep.configuration
    ) {
      await device.selectConfiguration(ep.configuration);
    }
    // Some Linux kernels claim the printer for the ``usblp`` driver; ask
    // the browser to detach it so we can transferOut() on the endpoint.
    try {
      await device.claimInterface(ep.interface);
    } catch (e) {
      throw new Error(
        `Couldn't claim USB interface (kernel driver may be holding it). Original: ${e.message}`,
      );
    }
    const res = await device.transferOut(ep.endpoint, bytes);
    return { bytesWritten: res.bytesWritten };
  } finally {
    try {
      await device.releaseInterface(ep.interface);
    } catch { /* best-effort */ }
    try {
      await device.close();
    } catch { /* best-effort */ }
  }
}

/**
 * Decode a base-64 payload from the backend into a Uint8Array suitable
 * for `sendBytes()`. Web browsers ship `atob` — no external dep needed.
 */
export function b64ToBytes(b64) {
  const bin = atob(b64 || "");
  const out = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
  return out;
}

/**
 * Fire any WebUSB browser-handoff results returned by the backend (e.g.
 * from `/cash-drawer/open` or `/printers/{id}/print-receipt`). Returns
 * ``{ fired, errors }`` — number of successful client-side transfers and
 * a list of "<printer name>: <error>" strings for the failed ones.
 */
export async function runBrowserHandoffs(handoffs) {
  let fired = 0;
  const errors = [];
  for (const h of handoffs || []) {
    if (!h?.payload_b64) continue;
    try {
      await sendBytes(
        {
          vendorId: h.webusb_vendor_id,
          productId: h.webusb_product_id,
          serialNumber: h.webusb_serial_number,
        },
        b64ToBytes(h.payload_b64),
      );
      fired += 1;
    } catch (e) {
      errors.push(`${h.name || "USB printer"}: ${e?.message || "WebUSB error"}`);
    }
  }
  return { fired, errors };
}

